import { drizzle, type NeonHttpDatabase } from 'drizzle-orm/neon-http';
import * as schema from './.drizzle/schema';
import * as relations from './.drizzle/relations';
import { sql } from './sql';
import type { Result, AppError } from '@shared/utils';
import { err, tryCatch } from '@shared/utils';

// Define the proper database type
type DatabaseSchema = typeof schema & typeof relations;
/**
 * @internal
 */
type DatabaseInstance = NeonHttpDatabase<DatabaseSchema>;

/**
 * @internal
 * @group Functions
 * @summary <blockquote>Creates a database connection using the Result pattern.</blockquote>
 */
function createDatabaseConnection(): Result<DatabaseInstance, AppError> {
  // First, get the SQL connection result
  if (sql.isErr) {
    const sqlError = sql.getFail();
    
    // Don't log errors for expected client-side or Next.js build scenarios
    const expectedErrors = ['DATABASE_SKIPPED_NEXTJS_BUILD', 'DATABASE_NOT_AVAILABLE_CLIENT_SIDE'];
    const errorCode = typeof sqlError.errorCode === 'string' ? sqlError.errorCode : '';
    const isExpectedError = expectedErrors.includes(errorCode);
    
    if (!isExpectedError && typeof console !== 'undefined') {
      // Only log unexpected database connection errors
      // eslint-disable-next-line @template/console-log
      console.error('Database connection failed:', {
        errorCode: sqlError.errorCode,
        errorMessage: sqlError.errorMessage
      });
    }
    
    return err({
      errorCode: sqlError.errorCode || 'DATABASE_CONNECTION_FAILED',
      errorMessage: sqlError.errorMessage
    });
  }

  const connection = sql.getSuccess();
  
  return tryCatch(() => {
    const database = drizzle(connection, {
      schema: {
        ...schema,
        ...relations
      }
    });
    return database;
  }).mapErr((error) => {
    // Use console.error during initialization (Logger not available yet to avoid circular dependencies)
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line @template/console-log
      console.error('Failed to create Drizzle database instance:', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
    
    return {
      errorCode: 'DRIZZLE_INITIALIZATION_FAILED',
      errorMessage: `Failed to initialize Drizzle ORM: ${error instanceof Error ? error.message : String(error)}`
    };
  });
}

// Create the database connection result
const dbResult = createDatabaseConnection();

/**
 * @group Functions
 * @summary <blockquote>Database connection result that can be used throughout the application.</blockquote>
 * 
 * @example
 * ```typescript
 * import { db } from '@shared/db';
 * 
 * db.match({
 *   ok: (database) => {
 *     const users = await database.select().from(usersTable);
 *   },
 *   err: (error) => {
 *     // Handle database connection error
 *     console.error('Database connection failed:', error.errorMessage);
 *   }
 * });
 * ```
 */
export const /**
 * @internal
 */
db: Result<DatabaseInstance, AppError> = dbResult;
